// ─── Data constants ───────────────────────────────────────────────────────────

export const INDUSTRIES = [
  {value: 'technology', label: 'Technology'},
  {value: 'finance-banking', label: 'Finance & Banking'},
  {value: 'healthcare', label: 'Healthcare'},
  {value: 'ecommerce-retail', label: 'E-commerce & Retail'},
  {value: 'manufacturing', label: 'Manufacturing'},
  {value: 'education', label: 'Education'},
  {value: 'media-entertainment', label: 'Media & Entertainment'},
  {value: 'consulting-services', label: 'Consulting & Services'},
  {value: 'real-estate', label: 'Real Estate'},
  {value: 'other', label: 'Other'},
]

export const COMPANY_SIZES = [
  {label: 'Sole trader', value: 'NOE_1'},
  {label: '1 - 10 employees', value: 'NOE_1_TO_10'},
  {label: '11 - 25 employees', value: 'NOE_11_TO_25'},
  {label: '26 - 50 employees', value: 'NOE_26_TO_50'},
  {label: '51 - 150 employees', value: 'NOE_51_TO_150'},
  {label: '151 - 300 employees', value: 'NOE_151_TO_300'},
  {label: 'More than 300 employees', value: 'NOE_MORE_THAN_300'},
]

export const RISK_OPTIONS = [
  {value: 'conservative', label: 'Conservative', desc: 'Strict limits, multiple approvals required'},
  {value: 'balanced', label: 'Balanced', desc: 'Reasonable limits with standard approval flow'},
  {value: 'aggressive', label: 'Aggressive', desc: 'Higher limits, streamlined approvals'},
]

export const GUIDELINES_OPTIONS = [
  {value: 'strict', label: 'Strict & policy-like', desc: 'Formal, policy-like language with clear mandates'},
  {value: 'empowering', label: 'Empowering & principle-based', desc: 'Principle-based guidance that trusts judgment'},
  {value: 'friendly', label: 'Friendly & trust-based', desc: 'Conversational, approachable guidelines'},
]

export const CATEGORY_OPTIONS = [
  {
    section: 'travel',
    sectionLabel: 'Travel',
    categories: [
      {value: 'FLIGHTS', label: 'Flights'},
      {value: 'HOTELS', label: 'Hotels'},
      {value: 'GROUND_TRANSPORT', label: 'Ground transport'},
      {value: 'CAR_RENTAL', label: 'Car rental'},
      {value: 'PER_DIEM', label: 'Per diem'},
      {value: 'RAIL', label: 'Rail & train'},
    ],
  },
  {
    section: 'meals',
    sectionLabel: 'Meals & Drinks',
    categories: [
      {value: 'CLIENT_DINNERS', label: 'Client dinners'},
      {value: 'TEAM_DINNERS', label: 'Team dinners'},
      {value: 'LUNCHES', label: 'Lunches'},
      {value: 'COFFEE_SNACKS', label: 'Coffee & snacks'},
    ],
  },
  {
    section: 'software',
    sectionLabel: 'Software & Subscriptions',
    categories: [
      {value: 'SAAS_TOOLS', label: 'SaaS tools'},
      {value: 'LICENSES', label: 'Software licenses'},
      {value: 'CLOUD_SERVICES', label: 'Cloud services'},
    ],
  },
  {
    section: 'office',
    sectionLabel: 'Office & Equipment',
    categories: [
      {value: 'HARDWARE_EQUIPMENT', label: 'Hardware & equipment'},
      {value: 'SUPPLIES', label: 'Office supplies'},
      {value: 'FURNITURE', label: 'Furniture'},
      {value: 'HOME_SETUP', label: 'Home office setup'},
    ],
  },
  {
    section: 'marketing',
    sectionLabel: 'Marketing & Events',
    categories: [
      {value: 'ADVERTISING', label: 'Advertising'},
      {value: 'EVENTS_CONFERENCES', label: 'Events & conferences'},
      {value: 'SWAG_MERCHANDISE', label: 'Swag & Merchandise'},
      {value: 'SPONSORSHIPS', label: 'Sponsorships'},
    ],
  },
  {
    section: 'employee-benefits',
    sectionLabel: 'Employee Benefits',
    categories: [
      {value: 'HEALTH_WELLNESS', label: 'Health & wellness'},
      {value: 'CHILDCARE', label: 'Childcare & family support'},
      {value: 'COMMUTER', label: 'Commuter benefits'},
      {value: 'MEAL_ALLOWANCES', label: 'Meal allowances'},
      {value: 'TEAM_BUILDING', label: 'Team building allowances'},
      {value: 'REFERRAL_BONUSES', label: 'Employee referral bonuses'},
    ],
  },
  {
    section: 'learning',
    sectionLabel: 'Learning & Development',
    categories: [
      {value: 'COURSES_CERTIFICATIONS', label: 'Courses & certifications'},
      {value: 'BOOKS_SUBSCRIPTIONS', label: 'Books & subscriptions'},
      {value: 'CONFERENCES_EVENTS', label: 'Conferences & events'},
      {value: 'COACHING_MENTORING', label: 'Coaching & mentoring'},
    ],
  },
  {
    section: 'professional-services',
    sectionLabel: 'Professional Services',
    categories: [
      {value: 'LEGAL_COMPLIANCE', label: 'Legal & compliance'},
      {value: 'FREELANCERS', label: 'Freelancers & contractors'},
      {value: 'CONSULTING', label: 'Consulting'},
    ],
  },
]

export const COUNTRY_PRESETS: Record<string, {code: string; label: string; cur: string}> = {
  de: {code: 'de', label: 'Germany', cur: '€'},
  nl: {code: 'nl', label: 'Netherlands', cur: '€'},
  gb: {code: 'gb', label: 'United Kingdom', cur: '£'},
  at: {code: 'at', label: 'Austria', cur: '€'},
  be: {code: 'be', label: 'Belgium', cur: '€'},
  fr: {code: 'fr', label: 'France', cur: '€'},
  ie: {code: 'ie', label: 'Ireland', cur: '€'},
  es: {code: 'es', label: 'Spain', cur: '€'},
  other: {code: 'other', label: 'None of these', cur: '€'},
}

export const THINKING_MESSAGES = [
  'Reviewing industry benchmarks',
  'Checking regional compliance',
  'Calibrating spend limits',
  'Preparing approval workflows',
  'Analysing category patterns',
  'Building policy structure',
]

export const PREP_MESSAGES = [
  'Warming up the policy engine',
  'Stretching our compliance muscles',
  'Ready when you are, boss',
  "Quietly judging other companies' policies",
  'Brewing coffee for the AI',
  'Rehearsing the word "reimbursement"',
]

// ─── Types ────────────────────────────────────────────────────────────────────

export type FormData = {
  companyName: string
  industry: string
  companySize: string
  country: string
  riskTolerance: string
  guidelinesStyle: string
  categories: string[]
}

// ─── Tailwind class constants ─────────────────────────────────────────────────

export const btn =
  'inline-flex items-center gap-2 px-[22px] py-[14px] bg-green-neon text-moss-black border-0 rounded-[6px] text-[11px] font-bold uppercase tracking-[0.08em] transition-[opacity,transform] duration-[220ms] whitespace-nowrap cursor-pointer hover:opacity-85 disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none'
export const btnGhost =
  'inline-flex items-center gap-2 px-[22px] py-[14px] bg-transparent text-moss-black border border-black/[0.18] rounded-[6px] text-[11px] font-bold uppercase tracking-[0.08em] transition-[opacity,transform] duration-[220ms] whitespace-nowrap cursor-pointer hover:bg-black/[0.04] hover:opacity-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none'
export const input =
  'w-full px-4 py-[14px] border border-black/[0.14] rounded-lg font-[inherit] text-[15px] text-moss-black bg-white outline-none transition-[border-color,box-shadow] duration-150 focus:border-moss-blue focus:shadow-[0_0_0_3px_rgba(44,143,255,0.14)] placeholder:text-black/35'
export const eyebrow =
  'text-[10px] font-bold uppercase tracking-[0.10em] text-black/55 inline-flex items-center gap-2 before:content-[""] before:w-[14px] before:h-px before:bg-current before:opacity-50'
export const skl =
  'bg-[length:400px_100%] bg-gradient-to-r from-black/[0.04] via-black/[0.08] to-black/[0.04] animate-epg-shimmer rounded h-3'
export const editable =
  'border-b border-dashed border-black/30 cursor-text px-[2px] hover:border-b-moss-black focus:outline-none focus:border-b focus:border-solid focus:border-moss-black'
export const doc =
  'bg-white border border-black/[0.08] rounded-[10px] shadow-[0_1px_0_rgba(0,0,0,0.02),0_12px_36px_rgba(0,0,0,0.06)]'
export const docHeader = 'px-8 pt-7 pb-[18px] border-b border-black/[0.06]'
export const docTitle = 'text-[22px] font-medium tracking-[-0.01em] text-moss-black'
export const docBody = 'px-8 py-5'
export const docSection = 'py-4 mb-4 border-b border-black/[0.04] last:border-b-0 rounded-lg'
export const docSectionHead = 'flex items-baseline justify-between gap-3'
export const docSectionTitle = 'text-[15px] font-semibold text-moss-black tracking-[-0.005em]'
export const docSectionBody =
  'text-[13px] leading-[1.55] text-black/65 mt-2 [&_strong]:text-moss-black [&_strong]:font-semibold' +
  ' [&_h6]:text-[10px] [&_h6]:uppercase [&_h6]:tracking-[0.08em] [&_h6]:font-semibold [&_h6]:text-black/40 [&_h6]:mt-[14px] [&_h6]:mb-[5px] [&_h6:first-child]:mt-0' +
  ' [&_ul]:list-disc [&_ul]:ml-4 [&_ul]:mt-0 [&_ul]:space-y-[3px]' +
  ' [&_mark]:bg-green-neon/[0.25] [&_mark]:text-moss-black [&_mark]:rounded [&_mark]:px-[3px] [&_mark]:font-medium [&_mark]:not-italic' +
  ' [&_p]:mt-1'
